import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import {
  FileText,
  Search,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  X
} from 'lucide-react';

const ExpenseRegister = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [ordering, setOrdering] = useState('-invoice_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [selected, setSelected] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);

  const [createForm, setCreateForm] = useState({
    invoice: '',
    type: 'operating',
    cost_center: '',
    pl_head: '',
    receiver_gstin: '',
    receiver_name: '',
    tax_invoice_no: '',
    invoice_date: '',
    igst: '',
    cgst: '',
    sgst: '',
    total_invoice_value: '',
    tds_rate: '',
    tds: '',
    bank_payment: '',
    date_of_payment: '',
    bank_name: '',
    remarks: ''
  });

  const [editForm, setEditForm] = useState({
    invoice: '',
    type: 'operating',
    cost_center: '',
    pl_head: '',
    receiver_gstin: '',
    receiver_name: '',
    tax_invoice_no: '',
    invoice_date: '',
    igst: '',
    cgst: '',
    sgst: '',
    total_invoice_value: '',
    tds_rate: '',
    tds: '',
    bank_payment: '',
    date_of_payment: '',
    bank_name: '',
    remarks: ''
  });

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const toMoney2 = (value, defaultValue = '0.00') => {
    const num = Number(value);
    if (Number.isNaN(num)) return defaultValue;
    return num.toFixed(2);
  };

  const exceedsMaxDigits = (moneyString) => {
    if (!moneyString) return false;
    const digitsOnly = moneyString.replace('.', '');
    return digitsOnly.length > 15;
  };

  const validateMoneyFields = (form) => {
    const moneyFields = [
      'igst',
      'cgst',
      'sgst',
      'total_invoice_value',
      'tds_rate',
      'tds',
      'bank_payment'
    ];
    for (const field of moneyFields) {
      const val = toMoney2(form[field] ?? '');
      if (exceedsMaxDigits(val)) return false;
    }
    return true;
  };

  const fetchItems = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (invoiceDate) params.append('invoice_date', invoiceDate);
      if (paymentDate) params.append('date_of_payment', paymentDate);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-invoice_date');

      const response = await axios.get(`${API_ENDPOINTS.EXPENSE_REGISTER_LIST}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : data.results || data.data || [];
      const normalizedCount = Array.isArray(data) ? data.length : data.count || normalized.length || 0;

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch expense register'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm((p) => ({ ...p, invoice_date: today, date_of_payment: today }));
    setEditForm((p) => ({ ...p, invoice_date: today, date_of_payment: today }));
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, ordering, invoiceDate, paymentDate]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchItems(1), 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm({
      invoice: '',
      type: 'operating',
      cost_center: '',
      pl_head: '',
      receiver_gstin: '',
      receiver_name: '',
      tax_invoice_no: '',
      invoice_date: today,
      igst: '',
      cgst: '',
      sgst: '',
      total_invoice_value: '',
      tds_rate: '',
      tds: '',
      bank_payment: '',
      date_of_payment: today,
      bank_name: '',
      remarks: ''
    });
    setIsCreateOpen(true);
    setError('');
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.tax_invoice_no) {
      setError('Please enter tax invoice number');
      return;
    }
    if (!validateMoneyFields(createForm)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsCreateLoading(true);
    try {
      const payload = {
        ...createForm,
        invoice: createForm.invoice ? parseInt(createForm.invoice) : null,
        igst: toMoney2(createForm.igst),
        cgst: toMoney2(createForm.cgst),
        sgst: toMoney2(createForm.sgst),
        total_invoice_value: toMoney2(createForm.total_invoice_value),
        tds_rate: toMoney2(createForm.tds_rate),
        tds: toMoney2(createForm.tds),
        bank_payment: toMoney2(createForm.bank_payment)
      };
      const response = await axios.post(API_ENDPOINTS.EXPENSE_REGISTER_CREATE, payload, { headers: getAuthHeaders() });
      const msg = response?.data?.message || 'Expense register entry created successfully';
      setSuccessMessage(msg);
      setIsCreateOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create expense register entry'));
    } finally {
      setIsCreateLoading(false);
    }
  };

  const handleView = async (row) => {
    try {
      setIsLoading(true);
      const response = await axios.get(API_ENDPOINTS.EXPENSE_REGISTER_DETAIL(row.id), { headers: getAuthHeaders() });
      setSelected(response.data);
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch expense register details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (row) => {
    try {
      setIsEditLoading(true);
      const response = await axios.get(API_ENDPOINTS.EXPENSE_REGISTER_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data || row;
      setSelected(data);
      setEditForm({
        invoice: data.invoice || '',
        type: data.type || 'operating',
        cost_center: data.cost_center || '',
        pl_head: data.pl_head || '',
        receiver_gstin: data.receiver_gstin || '',
        receiver_name: data.receiver_name || '',
        tax_invoice_no: data.tax_invoice_no || '',
        invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
        igst: data.igst || '',
        cgst: data.cgst || '',
        sgst: data.sgst || '',
        total_invoice_value: data.total_invoice_value || '',
        tds_rate: data.tds_rate || '',
        tds: data.tds || '',
        bank_payment: data.bank_payment || '',
        date_of_payment: data.date_of_payment ? data.date_of_payment.split('T')[0] : '',
        bank_name: data.bank_name || '',
        remarks: data.remarks || ''
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load expense register entry for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    if (!editForm.tax_invoice_no) {
      setError('Please enter tax invoice number');
      return;
    }
    if (!validateMoneyFields(editForm)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsEditLoading(true);
    try {
      const payload = {
        ...editForm,
        invoice: editForm.invoice ? parseInt(editForm.invoice) : null,
        igst: toMoney2(editForm.igst),
        cgst: toMoney2(editForm.cgst),
        sgst: toMoney2(editForm.sgst),
        total_invoice_value: toMoney2(editForm.total_invoice_value),
        tds_rate: toMoney2(editForm.tds_rate),
        tds: toMoney2(editForm.tds),
        bank_payment: toMoney2(editForm.bank_payment)
      };
      const response = await axios.put(
        API_ENDPOINTS.EXPENSE_REGISTER_UPDATE(selected.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Expense register entry updated successfully';
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update expense register entry'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(API_ENDPOINTS.EXPENSE_REGISTER_DELETE(deleteTarget.id), {
        headers: getAuthHeaders()
      });
      const msg = response?.data?.message || 'Expense register entry deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete expense register entry'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const filteredItems = items;

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Expenses</p>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Operating</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.type === 'operating').length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FileText className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Capital</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.type === 'capital').length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <FileText className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Revenue</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.type === 'revenue').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="operating">Operating</option>
            <option value="capital">Capital</option>
            <option value="revenue">Revenue</option>
            <option value="other">Other</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-invoice_date">Invoice Date: Newest</option>
            <option value="invoice_date">Invoice Date: Oldest</option>
            <option value="-date_of_payment">Payment Date: Newest</option>
            <option value="date_of_payment">Payment Date: Oldest</option>
            <option value="-total_invoice_value">Amount: High to Low</option>
            <option value="total_invoice_value">Amount: Low to High</option>
          </select>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            New Expense
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading expense register...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No expenses found</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax Invoice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receiver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Payable</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{row.tax_invoice_no || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{row.receiver_name || 'N/A'}</div>
                        <div className="text-xs text-muted-foreground">{row.receiver_gstin || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm capitalize text-gray-900">{row.type || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.date_of_payment ? new Date(row.date_of_payment).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(row.net_payable || row.bank_payment || row.total_invoice_value)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleView(row)} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(row)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDelete(row)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Showing page {currentPage} of {Math.ceil(totalCount / 10)}</div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage - 1)} disabled={!prevPage}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage + 1)} disabled={!nextPage}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Expense Register Details</h3>
                <p className="text-sm text-muted-foreground">Invoice #{selected.tax_invoice_no || 'N/A'}</p>
              </div>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><p className="text-muted-foreground">Receiver</p><p className="font-semibold">{selected.receiver_name || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Receiver GSTIN</p><p className="font-semibold">{selected.receiver_gstin || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Type</p><p className="font-semibold capitalize">{selected.type || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Cost Center</p><p className="font-semibold">{selected.cost_center || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">P&L Head</p><p className="font-semibold">{selected.pl_head || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Invoice Date</p><p className="font-semibold">{selected.invoice_date ? new Date(selected.invoice_date).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Payment Date</p><p className="font-semibold">{selected.date_of_payment ? new Date(selected.date_of_payment).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">IGST</p><p className="font-semibold">{formatCurrency(selected.igst)}</p></div>
                <div><p className="text-muted-foreground">CGST</p><p className="font-semibold">{formatCurrency(selected.cgst)}</p></div>
                <div><p className="text-muted-foreground">SGST</p><p className="font-semibold">{formatCurrency(selected.sgst)}</p></div>
                <div><p className="text-muted-foreground">Total Invoice Value</p><p className="font-semibold">{formatCurrency(selected.total_invoice_value)}</p></div>
                <div><p className="text-muted-foreground">TDS Rate</p><p className="font-semibold">{selected.tds_rate ? `${selected.tds_rate}%` : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">TDS</p><p className="font-semibold">{formatCurrency(selected.tds)}</p></div>
                <div><p className="text-muted-foreground">Bank Payment</p><p className="font-semibold">{formatCurrency(selected.bank_payment)}</p></div>
                <div><p className="text-muted-foreground">Net Payable</p><p className="font-semibold">{formatCurrency(selected.net_payable)}</p></div>
                <div><p className="text-muted-foreground">Bank Name</p><p className="font-semibold">{selected.bank_name || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Difference</p><p className="font-semibold">{formatCurrency(selected.difference)}</p></div>
                <div><p className="text-muted-foreground">GST %</p><p className="font-semibold">{selected.gst_percentage ? `${selected.gst_percentage}%` : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Created</p><p className="font-semibold">{selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Updated</p><p className="font-semibold">{selected.updated_at ? new Date(selected.updated_at).toLocaleString() : 'N/A'}</p></div>
              </div>

              {selected.remarks && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Remarks</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">{selected.remarks}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Create Expense Register Entry</h3>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice (ID)</label>
                  <input
                    type="number"
                    value={createForm.invoice}
                    onChange={(e) => setCreateForm((p) => ({ ...p, invoice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={createForm.type}
                    onChange={(e) => setCreateForm((p) => ({ ...p, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="operating">Operating</option>
                    <option value="capital">Capital</option>
                    <option value="revenue">Revenue</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Center</label>
                  <input
                    type="text"
                    value={createForm.cost_center}
                    onChange={(e) => setCreateForm((p) => ({ ...p, cost_center: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">P&L Head</label>
                  <input
                    type="text"
                    value={createForm.pl_head}
                    onChange={(e) => setCreateForm((p) => ({ ...p, pl_head: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receiver GSTIN</label>
                  <input
                    type="text"
                    value={createForm.receiver_gstin}
                    onChange={(e) => setCreateForm((p) => ({ ...p, receiver_gstin: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name</label>
                  <input
                    type="text"
                    value={createForm.receiver_name}
                    onChange={(e) => setCreateForm((p) => ({ ...p, receiver_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Invoice No</label>
                  <input
                    type="text"
                    value={createForm.tax_invoice_no}
                    onChange={(e) => setCreateForm((p) => ({ ...p, tax_invoice_no: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={createForm.invoice_date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IGST</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.igst}
                    onChange={(e) => setCreateForm((p) => ({ ...p, igst: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CGST</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.cgst}
                    onChange={(e) => setCreateForm((p) => ({ ...p, cgst: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SGST</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.sgst}
                    onChange={(e) => setCreateForm((p) => ({ ...p, sgst: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Invoice Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.total_invoice_value}
                    onChange={(e) => setCreateForm((p) => ({ ...p, total_invoice_value: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TDS Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.tds_rate}
                    onChange={(e) => setCreateForm((p) => ({ ...p, tds_rate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TDS</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.tds}
                    onChange={(e) => setCreateForm((p) => ({ ...p, tds: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Payment</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.bank_payment}
                    onChange={(e) => setCreateForm((p) => ({ ...p, bank_payment: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={createForm.date_of_payment}
                    onChange={(e) => setCreateForm((p) => ({ ...p, date_of_payment: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={createForm.bank_name}
                    onChange={(e) => setCreateForm((p) => ({ ...p, bank_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={createForm.remarks}
                  onChange={(e) => setCreateForm((p) => ({ ...p, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreateLoading}>
                  {isCreateLoading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Edit Expense Register Entry</h3>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice (ID)</label>
                  <input
                    type="number"
                    value={editForm.invoice}
                    onChange={(e) => setEditForm((p) => ({ ...p, invoice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm((p) => ({ ...p, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="operating">Operating</option>
                    <option value="capital">Capital</option>
                    <option value="revenue">Revenue</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Center</label>
                  <input
                    type="text"
                    value={editForm.cost_center}
                    onChange={(e) => setEditForm((p) => ({ ...p, cost_center: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">P&L Head</label>
                  <input
                    type="text"
                    value={editForm.pl_head}
                    onChange={(e) => setEditForm((p) => ({ ...p, pl_head: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receiver GSTIN</label>
                  <input
                    type="text"
                    value={editForm.receiver_gstin}
                    onChange={(e) => setEditForm((p) => ({ ...p, receiver_gstin: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name</label>
                  <input
                    type="text"
                    value={editForm.receiver_name}
                    onChange={(e) => setEditForm((p) => ({ ...p, receiver_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Invoice No</label>
                  <input
                    type="text"
                    value={editForm.tax_invoice_no}
                    onChange={(e) => setEditForm((p) => ({ ...p, tax_invoice_no: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={editForm.invoice_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IGST</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.igst}
                    onChange={(e) => setEditForm((p) => ({ ...p, igst: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CGST</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.cgst}
                    onChange={(e) => setEditForm((p) => ({ ...p, cgst: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SGST</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.sgst}
                    onChange={(e) => setEditForm((p) => ({ ...p, sgst: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Invoice Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.total_invoice_value}
                    onChange={(e) => setEditForm((p) => ({ ...p, total_invoice_value: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TDS Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.tds_rate}
                    onChange={(e) => setEditForm((p) => ({ ...p, tds_rate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TDS</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.tds}
                    onChange={(e) => setEditForm((p) => ({ ...p, tds: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Payment</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.bank_payment}
                    onChange={(e) => setEditForm((p) => ({ ...p, bank_payment: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={editForm.date_of_payment}
                    onChange={(e) => setEditForm((p) => ({ ...p, date_of_payment: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={editForm.bank_name}
                    onChange={(e) => setEditForm((p) => ({ ...p, bank_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={editForm.remarks}
                  onChange={(e) => setEditForm((p) => ({ ...p, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isEditLoading}>
                  {isEditLoading ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Expense Register Entry</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete expense {deleteTarget.tax_invoice_no || deleteTarget.id}?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseRegister;


